import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { appNeedsUpdate } from "@/redesign/appUpdate";
import { APP_STORE_URL, PLAY_STORE_URL } from "@/redesign/storeLinks";

/**
 * Full-screen "update the app" stop for a native build older than the site
 * allows (see appUpdate.ts for why). Checked on launch and every time the app
 * comes back to the foreground, so a member who updates and returns is let
 * straight through. Renders nothing on the web and nothing while unsure.
 *
 * Styled inline on purpose: it sits above the router, so it cannot rely on a
 * route chunk's stylesheet having loaded.
 */
export function NativeUpdateGate() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let alive = true;
    const check = () => {
      void appNeedsUpdate().then((needs) => {
        if (alive) setStale(needs);
      });
    };
    check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!stale) return null;
  const storeUrl =
    Capacitor.getPlatform() === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="update the app"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 24px",
        paddingTop: "calc(32px + env(safe-area-inset-top))",
        paddingBottom: "calc(32px + env(safe-area-inset-bottom))",
        background: "#834D4E",
        color: "#F4EDE5",
        textAlign: "center",
        fontFamily: "inherit",
      }}
    >
      <h2 style={{ fontSize: 26, fontWeight: 700, margin: 0, lineHeight: 1.2 }}>
        there's a newer zuzu app.
      </h2>
      <p
        style={{
          maxWidth: "32ch",
          margin: "14px 0 26px",
          opacity: 0.92,
          lineHeight: 1.45,
        }}
      >
        this version can't show your bookings and sessions properly any more.
        update it from the store and come straight back — your bookings and
        credits are safe.
      </p>
      <a
        href={storeUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: "inline-block",
          padding: "14px 26px",
          borderRadius: 999,
          background: "#F4EDE5",
          color: "#834D4E",
          fontWeight: 700,
          textDecoration: "none",
        }}
      >
        update now
      </a>
    </div>
  );
}
